import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('erotika_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('erotika_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      if (token) {
        try {
          // For guest users, just use localStorage data (don't overwrite purchases)
          const savedUser = localStorage.getItem('erotika_user');
          if (savedUser) {
            const parsed = JSON.parse(savedUser);
            if (parsed.email === 'guest@erotika.app') {
              setUser(parsed);
              setLoading(false);
              return;
            }
          }
          
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          localStorage.setItem('erotika_user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Auth init error:', error);
          if (error.response?.status === 401) {
            localStorage.removeItem('erotika_token');
            localStorage.removeItem('erotika_user');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (emailOrToken, passwordOrUser) => {
    // If passwordOrUser is an object, this is a guest/direct login
    if (typeof passwordOrUser === 'object') {
      const access_token = emailOrToken;
      const userData = passwordOrUser;
      localStorage.setItem('erotika_token', access_token);
      localStorage.setItem('erotika_user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return userData;
    }
    
    // Normal login with email/password
    const response = await axios.post(`${API}/auth/login`, { email: emailOrToken, password: passwordOrUser });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('erotika_token', access_token);
    localStorage.setItem('erotika_user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, username) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, username });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('erotika_token', access_token);
    localStorage.setItem('erotika_user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('erotika_token');
    localStorage.removeItem('erotika_user');
    localStorage.removeItem('erotika_guest_purchases');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        // For guest users, don't refresh from server (would overwrite local purchases)
        const currentUser = JSON.parse(localStorage.getItem('erotika_user') || 'null');
        if (currentUser?.email === 'guest@erotika.app') {
          return currentUser;
        }
        
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        localStorage.setItem('erotika_user', JSON.stringify(response.data));
        return response.data;
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
    return null;
  };

  // Update progress for a specific character
  const updateCharacterProgress = (characterId, progress) => {
    if (user && progress) {
      const updatedUser = {
        ...user,
        character_dialogues: {
          ...user.character_dialogues,
          [characterId]: progress.dialogues
        },
        character_levels: {
          ...user.character_levels,
          [characterId]: progress.unlocked_levels
        }
      };
      setUser(updatedUser);
      localStorage.setItem('erotika_user', JSON.stringify(updatedUser));
    }
  };

  // Helper functions
  const FREE_CHARACTERS = ['sofia', 'alexandre', 'sofia_alexandre'];

  const isGuest = user?.email === 'guest@erotika.app';

  // Guest purchase management via localStorage
  const getGuestPurchases = () => {
    try {
      return JSON.parse(localStorage.getItem('erotika_guest_purchases') || '{"characters":[],"levels":{}}');
    } catch { return { characters: [], levels: {} }; }
  };

  const addGuestPurchase = (itemType, itemId) => {
    const purchases = getGuestPurchases();
    if (itemType === 'character') {
      if (!purchases.characters.includes(itemId)) {
        purchases.characters.push(itemId);
      }
    } else if (itemType === 'level') {
      const [charId, level] = itemId.split(':');
      if (!purchases.levels[charId]) purchases.levels[charId] = [1];
      const lvl = parseInt(level);
      if (!purchases.levels[charId].includes(lvl)) {
        purchases.levels[charId].push(lvl);
        purchases.levels[charId].sort((a, b) => a - b);
      }
    }
    localStorage.setItem('erotika_guest_purchases', JSON.stringify(purchases));
    // Update local user state to reflect the purchase
    if (user && isGuest) {
      const updatedUser = { ...user };
      if (itemType === 'character') {
        updatedUser.unlocked_characters = [...(updatedUser.unlocked_characters || []), itemId];
      } else if (itemType === 'level') {
        const [charId, level] = itemId.split(':');
        const lvl = parseInt(level);
        updatedUser.character_levels = { ...updatedUser.character_levels };
        updatedUser.character_levels[charId] = [...(updatedUser.character_levels[charId] || [1]), lvl].sort((a, b) => a - b);
      }
      setUser(updatedUser);
      localStorage.setItem('erotika_user', JSON.stringify(updatedUser));
    }
  };

  const isCharacterUnlocked = (characterId) => {
    if (FREE_CHARACTERS.includes(characterId)) return true;
    if (user?.unlocked_characters?.includes(characterId)) return true;
    // Check guest purchases
    if (isGuest) {
      const guestPurchases = getGuestPurchases();
      return guestPurchases.characters.includes(characterId);
    }
    return false;
  };

  const isLevelUnlockedForCharacter = (characterId, level) => {
    if (level === 1) return true;
    const characterLevels = user?.character_levels?.[characterId] || [1];
    if (characterLevels.includes(level)) return true;
    // Check guest purchases
    if (isGuest) {
      const guestPurchases = getGuestPurchases();
      const guestLevels = guestPurchases.levels[characterId] || [1];
      return guestLevels.includes(level);
    }
    return false;
  };

  const getCharacterDialogues = (characterId) => {
    return user?.character_dialogues?.[characterId] || 0;
  };

  const getCharacterLevels = (characterId) => {
    return user?.character_levels?.[characterId] || [1];
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    updateCharacterProgress,
    isAuthenticated: !!token && !!user,
    isGuest,
    unlockedCharacters: user?.unlocked_characters || [],
    isCharacterUnlocked,
    isLevelUnlockedForCharacter,
    getCharacterDialogues,
    getCharacterLevels,
    addGuestPurchase,
    getGuestPurchases
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
